import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { FadeUp, Overline } from "@/components/Reveal";

const inputCls =
  "w-full bg-white border border-midnight/15 px-4 py-3 text-sm text-midnight placeholder:text-midnight/35 outline-none focus:border-gold transition-colors";

export default function Contact() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      trackEvent("contact_submitted");
      setSent(true);
    } catch {
      toast.error("Could not send your message. Please try the requirement form.");
    }
    setBusy(false);
  };

  return (
    <div className="bg-white min-h-screen" data-testid="contact-page">
      <Seo
        title="Contact — Royal Deals FZ-LLC"
        description="Contact Royal Deals FZ-LLC, a UAE-based advanced compute and technology supply company serving the GCC and international markets."
      />
      <Nav />
      <main className="pt-44 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <FadeUp>
            <Overline>Contact</Overline>
            <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-midnight">
              Talk to our commercial team.
            </h1>
            <p className="text-midnight/55 mt-6 max-w-md leading-relaxed">
              The fastest route is a structured requirement — it reaches the commercial team with everything needed to qualify availability.
            </p>
            <Link
              to="/request"
              data-testid="contact-rfq-link"
              className="inline-block mt-8 bg-midnight text-white text-sm font-medium px-8 py-4 hover:bg-midnight-light transition-colors"
            >
              Submit a Requirement →
            </Link>

            <div className="mt-14 bg-white hairline shadow-soft p-8">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-5">Registered Entity</p>
              <dl className="space-y-3 text-sm">
                <div className="flex gap-4"><dt className="text-midnight/40 w-28 font-bold text-xs uppercase tracking-wide pt-0.5">Entity</dt><dd className="text-midnight font-medium">ROYAL DEALS FZ-LLC</dd></div>
                <div className="flex gap-4"><dt className="text-midnight/40 w-28 font-bold text-xs uppercase tracking-wide pt-0.5">Licence</dt><dd className="text-midnight/75">Trade Licence No. 5040984</dd></div>
                <div className="flex gap-4"><dt className="text-midnight/40 w-28 font-bold text-xs uppercase tracking-wide pt-0.5">Registered</dt><dd className="text-midnight/75">VUEP1860, Compass Building – Al Hulaila, Al Hulaila Industrial Zone-FZ, Ras Al Khaimah, United Arab Emirates</dd></div>
                {settings?.contact_email && (
                  <div className="flex gap-4"><dt className="text-midnight/40 w-28 font-bold text-xs uppercase tracking-wide pt-0.5">Email</dt><dd className="text-midnight font-medium">{settings.contact_email}</dd></div>
                )}
                {settings?.phone && (
                  <div className="flex gap-4"><dt className="text-midnight/40 w-28 font-bold text-xs uppercase tracking-wide pt-0.5">Phone</dt><dd className="text-midnight/75">{settings.phone}</dd></div>
                )}
              </dl>
              {settings?.address && (
                <div className="mt-8 pt-6 border-t border-midnight/10" data-testid="contact-office-address">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">Dubai Office</p>
                  <p className="text-sm text-midnight/70 leading-relaxed whitespace-pre-line">{settings.address}</p>
                </div>
              )}
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            {sent ? (
              <div className="bg-white border border-gold/40 shadow-soft p-10" data-testid="contact-confirmation">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">Message Received</p>
                <p className="text-midnight/70 leading-relaxed">Thank you. Our team will respond to your enquiry.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white hairline shadow-soft p-8 space-y-5" data-testid="contact-form">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-midnight/40">General Enquiry</p>
                <input required placeholder="Name" value={form.name} onChange={set("name")} data-testid="contact-name" className={inputCls} />
                <input required type="email" placeholder="Business email" value={form.email} onChange={set("email")} data-testid="contact-email" className={inputCls} />
                <input placeholder="Company" value={form.company} onChange={set("company")} data-testid="contact-company" className={inputCls} />
                <textarea required rows={5} placeholder="Your message" value={form.message} onChange={set("message")} data-testid="contact-message" className={inputCls} />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="contact-submit"
                  className="w-full bg-midnight text-white text-sm font-semibold px-8 py-4 hover:bg-midnight-light transition-colors disabled:opacity-50"
                >
                  {busy ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </main>
      <Footer />
    </div>
  );
}
